import { useEffect, useMemo, useState } from 'react'
import { Box, Collapse, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Select from 'components/global/CUK/AsyncSelect/Select'
import { getEquipmentInfo, getNodeStatus } from '../utils'
import {
  SidebarPanel,
  SidebarHeaderBar,
  ActionButton,
  SidebarScrollable,
  TypeHeader,
  CountBadge,
} from '../Styled'

const STATUS_COLORS = {
  up: { fill: '#E6F0D7', stroke: '#92BC62' },
  down: { fill: '#FEE2E2', stroke: '#E57373' },
  missing_ref: { fill: '#E7E7E7', stroke: '#9CA3AF' },
}

const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? (
            <Box
              key={i}
              component="mark"
              sx={{ background: '#E9EAEC', color: 'inherit', borderRadius: '2px', px: '2px', fontWeight: 500 }}
            >
              {part}
            </Box>
          )
          : part
      )}
    </>
  )
}

const TreeItem = ({ equipment, onSelect, isSelected, isLast, isAboveTrunk, isTrunkBlue, searchQuery }) => {
  const { t } = useTranslation()
  const status = getNodeStatus(equipment.is_reachable)
  const { stroke } = STATUS_COLORS[status] || STATUS_COLORS.missing_ref
  const trunkLeftColor = isAboveTrunk ? '#2570EB' : '#EFEFEF'
  const branchBottomColor = isSelected ? '#2570EB' : '#EFEFEF'
  const trunkAfterColor = isTrunkBlue ? '#2570EB' : '#EFEFEF'

  const typeFallbackName = useMemo(() => {
    if (equipment.name) return null
    const info = getEquipmentInfo(equipment.type)
    if (info?.labelKey) return t(info.labelKey)
    return info?.label || null
  }, [equipment.name, equipment.type, t])

  const displayName = equipment.name || typeFallbackName || t('supervision.topology.unnamed')

  return (
    <Box
      onClick={() => onSelect(equipment.mac_address)}
      sx={{
        position: 'relative',
        pl: '14px',
        pr: 1,
        py: '1px',
        cursor: 'pointer',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          width: 14,
          height: '50%',
          borderLeft: `1px solid ${trunkLeftColor}`,
          borderBottom: `1px solid ${branchBottomColor}`,
          ...(isLast ? { borderBottomLeftRadius: 7 } : {}),
        },
        ...(!isLast && {
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            width: 0,
            height: '50%',
            borderLeft: `1px solid ${trunkAfterColor}`,
          },
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: '4px',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: stroke,
            border: `1.5px solid ${stroke}`,
            flexShrink: 0,
          }}
        />
        <Typography noWrap sx={{ fontSize: 12, fontWeight: isSelected ? 500 : 400, color: '#292929', flex: 1 }}>
          <HighlightText text={displayName} query={searchQuery} />
          {!equipment.name && (
            <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic', ml: 0.5, fontSize: 11 }}>
              (inconnu)
            </Box>
          )}
        </Typography>
        {equipment.mac_address && (
          <Typography sx={{ fontSize: 10, color: '#989898', fontFamily: 'Consolas, monospace', flexShrink: 0, ml: 0.5, letterSpacing: '-0.4px', fontWeight: isSelected ? 700 : 400 }}>
            <HighlightText text={equipment.mac_address} query={searchQuery} />
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const TypeSection = ({ typeInfo, equipments, onSelect, forceExpanded, selectedMac, searchQuery }) => {
  const [expanded, setExpanded] = useState(equipments.length <= 15)
  const { t } = useTranslation()
  const selectedIndex = selectedMac ? equipments.findIndex(eq => eq.mac_address === selectedMac) : -1
  const hasSelected = selectedIndex !== -1
  const chevronColor = hasSelected ? '#2570EB' : '#656565'

  useEffect(() => {
    if (forceExpanded !== null) setExpanded(forceExpanded)
  }, [forceExpanded])

  const label = typeInfo.labelKey ? t(typeInfo.labelKey) : (typeInfo.label || typeInfo.value)
  const isExpanded = hasSelected || expanded

  return (
    <Box>
      <TypeHeader onClick={() => setExpanded(v => !v)}>
        <i className={`fa-regular fa-angle-${isExpanded ? 'down' : 'right'} fa-sm`} style={{ width: 12, color: chevronColor }} />
        <i className={`${typeInfo.icon} fa-sm`} style={{ color: '#656565' }} />
        <Box sx={{ flex: 1, color: '#656565' }}>{label}</Box>
        <CountBadge>{equipments.length}</CountBadge>
      </TypeHeader>

      <Collapse in={isExpanded} timeout={200} unmountOnExit>
        <Box sx={{ ml: '23px' }}>
          {equipments.map((eq, idx) => (
            <TreeItem
              key={eq.mac_address}
              equipment={eq}
              isSelected={eq.mac_address === selectedMac}
              isLast={idx === equipments.length - 1}
              isAboveTrunk={hasSelected && idx <= selectedIndex}
              isTrunkBlue={hasSelected && idx < selectedIndex}
              onSelect={onSelect}
              searchQuery={searchQuery}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

const TopologySidebar = ({ equipments = [], existingTypes = [], onSelectEquipment, isExpanded, selectedMac, searchQuery = '' }) => {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState(null)
  const STATUS_OPTIONS = [
    { id: 'up', name: t('supervision.topology.status.up') },
    { id: 'down', name: t('supervision.topology.status.down') },
  ]

  const [sortAZ, setSortAZ] = useState(false)
  const [forceExpanded, setForceExpanded] = useState(false)

  const grouped = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const filtered = equipments.filter(eq => {
      if (statusFilter && statusFilter.id !== getNodeStatus(eq.is_reachable)) return false
      if (q) {
        const matchName = (eq.name || '').toLowerCase().includes(q)
        const matchMac = (eq.mac_address || '').toLowerCase().includes(q)
        if (!matchName && !matchMac) return false
      }
      return true
    })
    const groups = filtered.reduce((acc, eq) => {
      const key = eq.type || 'other'
      if (!acc[key]) acc[key] = []
      acc[key].push(eq)
      return acc
    }, {})
    if (sortAZ) {
      return Object.keys(groups).reduce((acc, k) => ({
        ...acc,
        [k]: [...groups[k]].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      }), {})
    }
    return groups
  }, [equipments, statusFilter, sortAZ, searchQuery])

  const typeInfoByValue = useMemo(() =>
    Object.fromEntries(existingTypes.map(t => [t.value, t])),
  [existingTypes])

  const handleExpandAll = () => setForceExpanded(prev => !prev)

  return (
    <SidebarPanel isExpanded={isExpanded}>
      <SidebarHeaderBar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{
          width: 152,
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 0.75,
          border: '1px solid #EFEFEF', borderRadius: '8px',
          px: 1, height: 32, overflow: 'hidden',
          '& [class$="-control"]': { border: 'none !important', boxShadow: 'none !important', minHeight: 'unset !important', background: 'transparent' },
          '& [class$="-placeholder"]': { color: '#464646' },
        }}>
          <i className="fa-regular fa-wave-pulse" style={{ color: '#464646', fontSize: 12, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0, '& > div': { width: '100%' } }}>
            <Select
              value={statusFilter}
              onChange={val => setStatusFilter(val && val.id ? val : null)}
              options={STATUS_OPTIONS}
              keyField="id"
              mainField="name"
              defaultPlaceholder={t('supervision.topology.sidebar.status_placeholder')}
              selectedPlaceholder="statut"
              isClearable
              hideSelectedOptions
              height={32}
              menuWidth="100%"
              menuPortalTarget={document.body}
              components={{ Input: () => null }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ActionButton onClick={() => setSortAZ(v => !v)}>
            <i className={`fa-regular ${sortAZ ? 'fa-arrow-up-a-z' : 'fa-arrow-down-a-z'}`} style={{ fontSize: 13 }} />
          </ActionButton>
          <ActionButton onClick={handleExpandAll}>
            <i className={`fa-regular ${forceExpanded ? 'fa-arrows-to-line' : 'fa-arrows-from-line'}`} style={{ fontSize: 13 }} />
          </ActionButton>
        </Box>
      </SidebarHeaderBar>

      <SidebarScrollable sx={{ pt: 0.5 }}>
        {Object.entries(grouped).map(([type, items]) => (
          <TypeSection
            key={type}
            typeInfo={typeInfoByValue[type] || getEquipmentInfo(type)}
            equipments={items}
            onSelect={onSelectEquipment}
            selectedMac={selectedMac}
            forceExpanded={forceExpanded}
            searchQuery={searchQuery}
          />
        ))}
        {Object.keys(grouped).length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>{t('supervision.topology.sidebar.no_equipment')}</Typography>
          </Box>
        )}
      </SidebarScrollable>
    </SidebarPanel>
  )
}

export default TopologySidebar


